import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Botao, Cartao, Selo } from '../../componentes/base';
import { Carregando, Erro } from '../../componentes/estados';
import { Rotulo, Selecao } from '../../componentes/formulario';
import { useToast } from '../../componentes/toast';
import { api, get, mensagemDeErro, post } from '../../nucleo/api';
import type { PropsCartaoNaFicha } from '../../nucleo/modulos-web';
import { useSessao } from '../../nucleo/sessao';
import { CampoEmpresaDaTela, useEmpresaDaTela } from '../../nucleo/empresa/EmpresaDaTela';
import { useContasFin } from './dados';
import { PROVEDORES_COBRANCA, type ConfigProvedor, type ProvedorCobranca } from './tipos';

/**
 * Trilha 2 (13/10, Pix Sicredi): qual banco gera a cobrança de cada fatura. Do mais específico ao mais geral: contrato,
 * cliente, empresa e o padrão do workspace. Mesmo cartão e controles da configuração do Asaas; nada grava no onChange.
 */
export function ProvedorConfig() {
  const { t } = useTranslation('contas');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const toast = useToast();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['/contas/cobranca/provedor'], queryFn: () => get<ConfigProvedor>('/contas/cobranca/provedor') });
  const contas = useContasFin();
  const [padrao, setPadrao] = useState<ProvedorCobranca>('asaas');
  const [contaId, setContaId] = useState('');
  // Rodada rval1-1 (RVAL1-ROB-M2): com a troca de empresa desligada não há empresa no topo; a regra da empresa é a da escolhida
  // aqui (lida e gravada pela rota da regra de empresa, a mesma da ficha da empresa).
  const daTela = useEmpresaDaTela();
  const regraDaTela = useQuery({
    queryKey: ['/contas/cobranca/provedor', 'empresa', daTela.empresa],
    queryFn: () => get<{ provedor: ProvedorCobranca | null }>(`/contas/cobranca/provedor/empresa/${daTela.empresa}`),
    enabled: daTela.mostra && !!daTela.empresa,
  });
  const empresaDaRegra = daTela.mostra ? daTela.empresa : (q.data?.empresa_id ?? null);
  // Review rval1-2 robustez (B1): a regra gravada da empresa escolhida (null enquanto carrega) e a edição presa à empresa em
  // que foi feita. Trocar a empresa no campo recomeça do valor gravado da nova (antes o select ficava com o da anterior e o
  // Salvar o gravava na nova); enquanto a regra não chega, o select e o Salvar ficam bloqueados (salvar antes apagava a regra).
  const regraGravada: ProvedorCobranca | '' | null = daTela.mostra
    ? regraDaTela.data
      ? (regraDaTela.data.provedor ?? '')
      : null
    : q.data
      ? (q.data.provedor_empresa ?? '')
      : null;
  // Review rval1-3 robustez (B1): a regra da empresa que falhou ao carregar mostra o erro com "tentar de novo" e não trava o
  // Salvar do padrão do workspace (a regra da empresa só é gravada depois de carregada).
  const regraFalhou = daTela.mostra && regraDaTela.isError;
  const regraPronta = !empresaDaRegra || regraGravada !== null || regraFalhou;
  const [editado, setEditado] = useState<{ empresa: string; valor: ProvedorCobranca | '' } | null>(null);
  const daEmpresa: ProvedorCobranca | '' = editado && editado.empresa === empresaDaRegra ? editado.valor : (regraGravada ?? '');
  useEffect(() => {
    if (!q.data) return;
    setPadrao(q.data.provedor_padrao);
    setContaId(q.data.sicredi_conta_id ?? '');
  }, [q.data]);
  const salvar = useMutation({
    mutationFn: async () => {
      if (q.data?.pode_padrao) await api('PUT', '/contas/cobranca/provedor', { provedor_padrao: padrao, sicredi_conta_id: contaId || null });
      // Só grava a regra da empresa já carregada (antes de carregar, o valor da tela não é o dela).
      if (empresaDaRegra && regraGravada !== null) await api('PUT', `/contas/cobranca/provedor/empresa/${empresaDaRegra}`, { provedor: daEmpresa || null });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['/contas/cobranca/provedor'] });
      toast.sucesso(tn('comum.salvo'));
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const registrar = useMutation({
    mutationFn: () => post('/contas/cobranca/sicredi/webhook', {}),
    onSuccess: () => toast.sucesso(t('provedor.registrado')),
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const podeEditar = s.pode('contas.configurar');
  // Review r46 (VIS-B1): o Salvar só aparece quando há o que gravar (o padrão do workspace, para quem vê todas, ou o da
  // empresa do seletor); o restrito em "Todas as empresas" via o toast "Salvo" sem requisição nenhuma. O registro do
  // webhook do conector de todas as empresas também é só de quem vê todas (o restrito registra o da empresa dele).
  // Review rval1-4 (ROB-B1, SPEC-B1): com a regra da empresa em erro, quem não grava o padrão não tem o que salvar (o Salvar
  // some e fica o erro com "tentar de novo"; antes o clique dava "Salvo" sem requisição, contra a r46 VIS-B1).
  const temOQueSalvar = !!q.data && (q.data.pode_padrao || (!!empresaDaRegra && !regraFalhou));
  const podeRegistrar = !!q.data && q.data.sicredi_conectado && (q.data.pode_padrao || !!q.data.sicredi_da_empresa);
  const opcoes = PROVEDORES_COBRANCA.map((p) => ({ valor: p, rotulo: t(`provedor.opcoes.${p}`) }));
  const opcoesConta = (contas.data?.itens ?? []).filter((c) => c.moeda === 'BRL').map((c) => ({ valor: c.id, rotulo: c.nome }));

  return (
    <Cartao titulo={t('provedor.titulo')}>
      {q.isPending && <Carregando />}
      {q.isError && <Erro erro={q.error} aoTentar={() => q.refetch()} />}
      {q.data && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3" data-provedor-config>
          <p className="text-sm text-tinta-3 md:col-span-3">
            {t('provedor.explicacao')}{' '}
            {q.data.sicredi_conectado ? (
              <Selo cor={q.data.sicredi_ambiente === 'producao' ? 'verde' : 'neutro'}>{t(`provedor.ambiente.${q.data.sicredi_ambiente ?? 'homologacao'}`)}</Selo>
            ) : (
              <Link to="/integracoes/integracoes/conectores" className="-my-1 inline-block py-1 font-medium text-acento-forte hover:underline">
                {t('provedor.conectarSicredi')}
              </Link>
            )}
          </p>
          <Rotulo id="provedor-padrao" rotulo={t('provedor.padrao')} ajuda={q.data.pode_padrao ? t('provedor.padraoAjuda') : t('provedor.padraoSoTodas')}>
            <Selecao id="provedor-padrao" aria-describedby="provedor-padrao-ajuda" disabled={!podeEditar || !q.data.pode_padrao} value={padrao} onChange={(e) => setPadrao(e.target.value as ProvedorCobranca)} opcoes={opcoes} />
          </Rotulo>
          {daTela.mostra && (
            <div className="md:col-span-3">
              <CampoEmpresaDaTela id="provedor-empresa-da-tela" estado={daTela} />
            </div>
          )}
          {empresaDaRegra ? (
            <Rotulo id="provedor-empresa" rotulo={t('provedor.empresa')} ajuda={t('provedor.empresaAjuda')}>
              <Selecao id="provedor-empresa" aria-describedby="provedor-empresa-ajuda" disabled={!podeEditar || !regraPronta || regraFalhou} value={daEmpresa} onChange={(e) => setEditado({ empresa: empresaDaRegra, valor: e.target.value as ProvedorCobranca | '' })} vazio={t('provedor.segueWorkspace')} opcoes={opcoes} />
            </Rotulo>
          ) : (
            <p className="self-center text-xs text-tinta-3">{t('provedor.escolhaEmpresa')}</p>
          )}
          {regraFalhou && (
            <div className="md:col-span-3">
              <Erro erro={regraDaTela.error} aoTentar={() => regraDaTela.refetch()} />
            </div>
          )}
          <Rotulo id="provedor-conta" rotulo={t('provedor.contaSicredi')} ajuda={t('provedor.contaSicrediAjuda')}>
            <Selecao id="provedor-conta" aria-describedby="provedor-conta-ajuda" disabled={!podeEditar || !q.data.pode_padrao} value={contaId} onChange={(e) => setContaId(e.target.value)} vazio={t('asaas.contaPadrao')} opcoes={opcoesConta} />
          </Rotulo>
          {podeEditar && (temOQueSalvar || podeRegistrar) && (
            <div className="flex flex-wrap items-center gap-2 md:col-span-3">
              {temOQueSalvar && (
                <Botao variante="secundario" carregando={salvar.isPending} ocupado={!regraPronta} onClick={() => salvar.mutate()}>
                  {tn('comum.salvar')}
                </Botao>
              )}
              {podeRegistrar && (
                <Botao variante="secundario" carregando={registrar.isPending} onClick={() => registrar.mutate()}>
                  {t('provedor.registrarWebhook')}
                </Botao>
              )}
              {podeRegistrar && <span className="text-xs text-tinta-3">{t('provedor.registrarAjuda')}</span>}
            </div>
          )}
        </div>
      )}
    </Cartao>
  );
}

/** Regra de um cliente ou de um contrato: o select começa no que está gravado e só grava no Salvar. */
export function CartaoProvedorRegistro({ alvo, id }: { alvo: 'parceiro' | 'contrato'; id: string }) {
  const { t } = useTranslation('contas');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const toast = useToast();
  const qc = useQueryClient();
  const chave = `/contas/cobranca/provedor/${alvo}/${id}`;
  const ver = s.pode('contas.ver');
  const q = useQuery({ queryKey: [chave], queryFn: () => get<{ provedor: ProvedorCobranca | null; pode_editar?: boolean }>(chave), enabled: ver });
  const [valor, setValor] = useState<ProvedorCobranca | ''>('');
  useEffect(() => {
    if (q.data) setValor(q.data.provedor ?? '');
  }, [q.data]);
  const salvar = useMutation({
    mutationFn: () => api('PUT', chave, { provedor: valor || null }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: [chave] });
      toast.sucesso(tn('comum.salvo'));
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  if (!ver) return null;
  // Review r46 (VIS-B1): o servidor diz se quem pede muda a regra deste registro (cliente de todas as empresas: só quem vê todas).
  const soTodas = q.data?.pode_editar === false;
  const podeEditar = s.pode('contas.configurar') && !soTodas;
  const campo = `provedor-${alvo}-${id}`;
  return (
    <Cartao titulo={t(`provedor.${alvo}.titulo`)}>
      {q.isPending && <Carregando />}
      {q.isError && <Erro erro={q.error} aoTentar={() => q.refetch()} />}
      {q.data && (
        <div className="flex min-w-0 flex-col gap-3" data-provedor-registro={alvo}>
          <Rotulo id={campo} rotulo={t('provedor.banco')} ajuda={soTodas ? t('provedor.registroSoTodas') : t(`provedor.${alvo}.ajuda`)}>
            <Selecao
              id={campo}
              aria-describedby={`${campo}-ajuda`}
              disabled={!podeEditar}
              value={valor}
              onChange={(e) => setValor(e.target.value as ProvedorCobranca | '')}
              vazio={t(`provedor.${alvo}.segue`)}
              opcoes={PROVEDORES_COBRANCA.map((p) => ({ valor: p, rotulo: t(`provedor.opcoes.${p}`) }))}
            />
          </Rotulo>
          {podeEditar && (
            <div>
              <Botao variante="secundario" carregando={salvar.isPending} onClick={() => salvar.mutate()}>
                {tn('comum.salvar')}
              </Botao>
            </div>
          )}
        </div>
      )}
    </Cartao>
  );
}

/** Cartão "Cobrança" na ficha do cliente (Cadastros), pelo encaixe dos módulos na ficha. */
export function CartaoCobrancaCliente({ parceiroId }: PropsCartaoNaFicha) {
  return <CartaoProvedorRegistro alvo="parceiro" id={parceiroId} />;
}
